import { Badge, Box, Group, Stack, Text, Title, type BoxProps } from "@mantine/core";
import type { ReactNode } from "react";
import { fmtNumber, macroSplit } from "@passio/api-client";

/** Small mono label with a violet dot, as used above section titles on the homepage. */
export function Eyebrow({ children, ...props }: { children: ReactNode } & BoxProps) {
  return (
    <Box component="span" className="pd-eyebrow" {...props}>
      {children}
    </Box>
  );
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-end" mb="lg" wrap="wrap" gap="md">
      <Stack gap={6} maw={760}>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <Title order={1} fz={{ base: 28, sm: 34 }}>
          {title}
        </Title>
        {description && (
          <Text c="dimmed" fz="md">
            {description}
          </Text>
        )}
      </Stack>
      {actions}
    </Group>
  );
}

/** Dark card with mono caption + big title, like the "Chicken burrito bowl" hero card. */
export function StatCard({ caption, title, stats, accent, children }: { caption: string; title: ReactNode; stats?: { value: string; label: string }[]; accent?: boolean; children?: ReactNode }) {
  return (
    <div className={`pd-stat-card${accent ? " accent" : ""}`}>
      <div className="pd-mono">{caption}</div>
      <Text fw={700} fz={20} mt={6} mb={stats ? 10 : 0} c="white">
        {title}
      </Text>
      {stats && (
        <Group gap="lg">
          {stats.map((s) => (
            <div key={s.label} className="pd-mono" style={{ color: "#fff" }}>
              <div>{s.value}</div>
              <div style={{ color: "var(--pd-ink-muted)" }}>{s.label}</div>
            </div>
          ))}
        </Group>
      )}
      {children}
    </div>
  );
}

export function MacroBar({ protein, carbs, fat }: { protein: number; carbs: number; fat: number }) {
  const s = macroSplit({ protein, carbs, fat });
  return (
    <div className="pd-macro-bar" role="img" aria-label={`Calories from protein ${s.protein}%, carbs ${s.carbs}%, fat ${s.fat}%`}>
      <span style={{ width: `${s.protein}%`, background: "var(--pd-protein)" }} />
      <span style={{ width: `${s.carbs}%`, background: "var(--pd-carbs)" }} />
      <span style={{ width: `${s.fat}%`, background: "var(--pd-fat)" }} />
    </div>
  );
}

export function MacroChips({ calories, protein, carbs, fat, grams, size = "sm" }: { calories?: number; protein?: number; carbs?: number; fat?: number; grams?: number; size?: "xs" | "sm" | "md" }) {
  return (
    <Group gap={6} wrap="wrap">
      {calories !== undefined && (
        <Badge size={size} color="ink.8" variant="filled">
          {fmtNumber(calories)} kcal
        </Badge>
      )}
      {protein !== undefined && (
        <Badge size={size} variant="light" color="violet">
          P {fmtNumber(protein, 1)}g
        </Badge>
      )}
      {carbs !== undefined && (
        <Badge size={size} variant="light" color="lime.8">
          C {fmtNumber(carbs, 1)}g
        </Badge>
      )}
      {fat !== undefined && (
        <Badge size={size} variant="light" color="orange">
          F {fmtNumber(fat, 1)}g
        </Badge>
      )}
      {grams ? (
        <Badge size={size} variant="outline" color="gray">
          ≈ {fmtNumber(grams)} g
        </Badge>
      ) : null}
    </Group>
  );
}

export function MacroLegend() {
  return (
    <Group gap="md" fz="xs" c="dimmed">
      {[
        ["Protein", "var(--pd-protein)"],
        ["Carbs", "var(--pd-carbs)"],
        ["Fat", "var(--pd-fat)"],
      ].map(([l, c]) => (
        <Group key={l} gap={6}>
          <Box w={10} h={10} style={{ borderRadius: 3, background: c }} />
          <Text fz="xs" c="dimmed">
            {l}
          </Text>
        </Group>
      ))}
    </Group>
  );
}
